import React, { useEffect, useState } from 'react';
import Button from '@material-ui/core/Button';
import { makeStyles, type Theme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import BackIcon from '@material-ui/icons/KeyboardBackspace';
import { colors } from '@skyslope/mache';
import { useParams, useHistory } from 'react-router-dom';
import useUserInfo from '../../auth/useUserInfo';
import { withLaunchDarkly, type LaunchDarklyFlags } from '../../common/launchDarkly';
import NavHeader from '../../common/navigation/NavHeader';
import { Recipients } from '../../common/recipients';
import { type IRecipient, RecipientRole } from '../../common/recipients/types';
import { checkIfRecipientIsSender } from '../../common/utils';
import Footer from '../../components/common/Footer';
import FooterLinks from '../../components/common/FooterLinks';
import Spinner from '../../components/common/Spinner';
import { HeaderContext } from '../../context/header-context';
import { getUsersGroups } from '../../lib/api/account/accountApi';
import { preferencesUrl } from '../../lib/environment-config';
import { useShallowSelector, useDispatch } from '../../lib/reduxHooks';
import { UserOrigins } from '../../lib/types';
import { applyColorAndCounterToNewRecipient, sortRecipients } from '../../lib/utils';
import type { IRootState } from '../../store';
import * as actions from '../../store/dtm/recipients/actions';

const useStyles = makeStyles((theme: Theme) => ({
  wrapper: {
    width: '100%',
    maxWidth: 1200,
    maxHeight: 639,
    height: '100%',
  },
  header: {
    padding: theme.spacing(4),
    textAlign: 'center',
  },
  gridModal: {
    gridTemplateColumns: '380px 1fr',
    border: 'none',
  },
  grid: {
    backgroundColor: 'white',
    border: `1px solid ${colors.grey[400]}`,
    display: 'grid',
    gridTemplateColumns: '530px 1fr',
    gridAutoFlow: 'column',
    minHeight: '55vh',
  },
  backButton: {
    marginRight: 'auto',
  },
  nextButton: {
    padding: '10px 40px',
  },
}));

function DtmRecipientsManagement({ flags }: { flags: LaunchDarklyFlags }) {
  const dispatch = useDispatch();
  const history = useHistory();
  const { id: envelopeId } = useParams<{ id: string }>();

  const { recipients, optionalRecipients, isFetching, done, contacts, colorCounter, settings, settingsFetched } =
    useShallowSelector((state: IRootState) => ({
      recipients: state.dtm.recipients.recipients,
      optionalRecipients: state.dtm.recipients.optionalRecipients,
      isFetching: state.dtm.recipients.isFetching,
      done: state.dtm.recipients.done,
      contacts: state.dtm.recipients.contacts,
      colorCounter: state.pageFrame.recipientColorCounter,
      settings: state.pageFrame.settings,
      settingsFetched: state.pageFrame.settingsFetched,
    }));

  const userInfo = useUserInfo();
  const classes = useStyles();

  const [init, setInit] = useState(false);
  const headerContext = React.useContext(HeaderContext);

  const hideNavItems = flags && flags['hide-build-ui-elements'];
  const showPreferencesMenuItem = flags && flags['add-automatic-time-stamp-signatures-to-user-preferences'];
  const isStartSafeAccount = settingsFetched && settings?.account?.userOrigin === UserOrigins.DOP;
  const showAutomatedRemindersFlag = Boolean(
    flags && flags['automated-reminders'] && settingsFetched && !isStartSafeAccount
  );

  useEffect(() => {
    if (init || isFetching || !userInfo) return;

    // If sender is already an optional recipient, we done.
    if (optionalRecipients.some((o) => checkIfRecipientIsSender(o))) {
      setInit(true);
      return;
    }

    let baseSender = {
      id: userInfo.senderId,
      role: RecipientRole.Signer,
      email: userInfo.senderEmail,
      isSender: true,
      color: colors.blue[800],
      isActive: false,
      isPlaceholder: false,
      title: 'Me',
      firstName: userInfo.senderFirstName,
      lastName: userInfo.senderLastName,
    };

    // If Sender is already a Recipient
    const recipientIndex = recipients.findIndex((r: IRecipient) => r.title?.toLowerCase() === 'me');
    if (recipientIndex > -1) {
      // If the recipient has no email, we need to update the recipient
      if (!recipients[recipientIndex].email) {
        baseSender.id = recipients[recipientIndex].id;
        // @ts-expect-error -- This code was written prior to enforcing lint rules
        baseSender.group = { id: 'default', name: 'default', order: 0 };
        baseSender.isActive = true;
        baseSender.isPlaceholder = true;
        // @ts-expect-error -- This code was written prior to enforcing lint rules
        baseSender.recipientColorCounter = recipients[recipientIndex].recipientColorCounter;
        const newRecipients = [...recipients];
        // @ts-expect-error -- This code was written prior to enforcing lint rules
        newRecipients.splice(recipientIndex, 1, baseSender);
        dispatch(actions.updateRecipients(newRecipients));
        // Otherwise we just use the real recipient to improve our optional recipient
      } else {
        baseSender = {
          ...recipients[recipientIndex],
          isSender: true,
          // @ts-expect-error -- This code was written prior to enforcing lint rules
          group: recipients[recipientIndex].group ?? { id: 'default', name: 'default', order: 0 },
          color: colors.blue[800],
        };
      }
    }
    // Add sender to optional recipients
    const newORs = [baseSender, ...optionalRecipients];
    // @ts-expect-error -- This code was written prior to enforcing lint rules
    dispatch(actions.updateOptionalRecipients(newORs));
    setInit(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This code was written prior to enforcing lint rules
  }, [recipients, optionalRecipients, userInfo, init, isFetching]);

  useEffect(() => {
    headerContext.setContent(
      <NavHeader
        labelName="Add recipients"
        breadcrumb={{ name: 'Envelope Management', onClick: () => history.push('/envelopes') }}
        progressBarPercent={80}
        hideNavItems={hideNavItems}
        preferencesUrl={preferencesUrl}
        showPreferences={showPreferencesMenuItem}
        getUsersGroups={getUsersGroups}
        flags={flags}
      />
    );

    dispatch(actions.fetch(envelopeId, userInfo?.impersonatedPrimeUserGuid));
    if (!contacts.length) {
      dispatch(actions.fetchContacts());
    }

    return () => {
      headerContext.setContent(null);
      dispatch(actions.reset());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This code was written prior to enforcing lint rules
  }, []);

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const prepareRecipients = () => {
    const updatedRecipients = [...recipients];
    optionalRecipients.forEach((or: IRecipient) => {
      if (!updatedRecipients.find((ur) => ur.id === or.id) && !checkIfRecipientIsSender(or) && or.isPlaceholder) {
        updatedRecipients.push(or);
      }
    });
    dispatch(actions.updateRecipients(updatedRecipients));
  };

  async function handleNextButton() {
    prepareRecipients();
    dispatch(actions.save(envelopeId));
  }

  function handleCreatePrimeContact(contact: {
    firstName: string;
    lastName: string;
    email: string;
    optional: boolean;
    id: string;
  }) {
    dispatch(actions.createPrimeContact(contact));
  }

  function onChange(newRecipients: IRecipient[]) {
    applyColorAndCounterToNewRecipient(newRecipients, recipients, colorCounter);
    sortRecipients(newRecipients);
    dispatch(actions.updateRecipients(newRecipients));
  }

  // Filter incompletePlaceholdersProp from state
  const incompletePlaceholdersProp = React.useMemo(() => {
    return recipients?.filter(
      (r) => r.isPlaceholder && (r.firstName === null || r.email === null) && r.title?.toLowerCase() !== 'me'
    );
  }, [recipients]);

  if (done) {
    history.push(`/envelopes/${envelopeId}`);
  }

  if (isFetching) return <Spinner />;

  const nextDisabled = !recipients.length || !recipients.some((r) => r.role === RecipientRole.Signer && r.isActive);

  return (
    <div className={classes.wrapper}>
      <div className={classes.header}>
        <Typography variant="h3">Recipients & signing order</Typography>
        <Typography component="span" variant="body2" style={{ marginTop: 8 }}>
          Add recipients by clicking on the “+ New Recipient” button in the left side panel
        </Typography>
      </div>
      <div className={classes.grid}>
        <Recipients
          envelopeId={envelopeId}
          recipients={recipients}
          onChange={onChange}
          optionalRecipients={optionalRecipients}
          contacts={contacts}
          createPrimeContact={handleCreatePrimeContact}
          incompletePlaceholders={incompletePlaceholdersProp}
          colorCounter={colorCounter}
          showAutomatedRemindersFlag={showAutomatedRemindersFlag}
        />
      </div>
      <Footer>
        {hideNavItems ? null : (
          <>
            <Button
              className={classes.backButton}
              color="primary"
              onClick={() => history.push(`/dtm/envelopes/${envelopeId}/documents`)}
            >
              <BackIcon fontSize="small" /> Back
            </Button>
            <Button color="primary" data-spec="exit" onClick={() => history.push('/envelopes')}>
              Exit
            </Button>
          </>
        )}
        <Button
          variant="contained"
          color="primary"
          data-spec="next"
          onClick={handleNextButton}
          className={classes.nextButton}
          disabled={nextDisabled}
        >
          Next
        </Button>
      </Footer>
      <FooterLinks />
    </div>
  );
}

// eslint-disable-next-line import/no-default-export -- This code was written prior to enforcing lint rules
export default withLaunchDarkly(DtmRecipientsManagement);
